import cn from "classnames";
import { MediaDirectoryKeys } from "constants/enums";
import { t } from "i18next";
import { useAudioRecorder } from "libs/audioRecorder";
import { useDirectUploadToS3 } from "libs/directUpload";
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import { Tooltip } from "react-tooltip";

import { useImproveMessage } from "../../apis";
import styles from "./ChatComposer.module.scss";

interface Props {
  onSend?: (payload: {
    text?: string;
    imageUrl?: string;
    audioUrl?: string;
  }) => void;
  disabled?: boolean;
  disabledReason?: string;
}

const TOOLTIP_ANCHOR_ID = "ai-conversations-composer-disabled-anchor";

const AttachIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M12.917 6.146 7.708 11.354a1.77 1.77 0 0 0 2.5 2.5l5.209-5.208a3.54 3.54 0 0 0-5-5l-5.209 5.208a5.3 5.3 0 0 0 7.5 7.5l4.792-4.791"
      stroke="#7E7E8B"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect
      x="7.5"
      y="2.5"
      width="5"
      height="9"
      rx="2.5"
      stroke="#7E7E8B"
      strokeWidth="1.4"
    />
    <path
      d="M4.583 8.75a5.417 5.417 0 0 0 10.834 0M10 14.167v3.333"
      stroke="#7E7E8B"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StopIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="3" y="3" width="10" height="10" rx="2" fill="#fb3973" />
  </svg>
);

const CloseIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M1.5 1.5L8.5 8.5M8.5 1.5L1.5 8.5"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const SendIcon = ({ active }: { active: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M2 10L18 2L10 19L8 11.5L2 10Z"
      fill={active ? "#fff" : "#7e7e8b"}
      stroke={active ? "#fff" : "#7e7e8b"}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const ImproveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M11.2 2.6 12.9 7.1 17.4 8.8 12.9 10.5 11.2 15 9.5 10.5 5 8.8 9.5 7.1Z"
      fill="currentColor"
    />
    <path
      d="M4.6 2 5.2 3.6 6.8 4.2 5.2 4.8 4.6 6.4 4 4.8 2.4 4.2 4 3.6Z"
      fill="currentColor"
    />
    <path
      d="M15.4 12.2 15.9 13.5 17.2 14 15.9 14.5 15.4 15.8 14.9 14.5 13.6 14 14.9 13.5Z"
      fill="currentColor"
    />
  </svg>
);

const DismissIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const ChatComposer = ({ onSend, disabled, disabledReason }: Props) => {
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { uploader, isLoading: isUploading } = useDirectUploadToS3();
  const { isRecording, start, stop, cancel } = useAudioRecorder();
  const improveMutation = useImproveMessage();
  const isImproving = improveMutation.isLoading;

  // Only one media kind may be staged at a time.
  const hasMedia = Boolean(imageUrl) || Boolean(audioUrl);

  useEffect(() => {
    if (!isRecording) {
      setRecordSeconds(0);
      return;
    }
    const id = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAttachClick = () => {
    if (disabled || isUploading || isRecording || hasMedia) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploader(file, {
      directoryKey: MediaDirectoryKeys.Admin,
      onSuccess: (media) => setImageUrl(media.url),
      onError: () => setImageUrl(null),
    });
    resetFileInput();
  };

  const handleMicClick = async () => {
    if (disabled || isUploading) return;
    if (isRecording) {
      const file = await stop();
      await uploader(file, {
        directoryKey: MediaDirectoryKeys.Admin,
        onSuccess: (media) => setAudioUrl(media.url),
        onError: () => setAudioUrl(null),
      });
      return;
    }
    if (hasMedia) return;
    try {
      await start();
    } catch {
      toast.error(
        t(
          "Microphone access is blocked. Allow it in your browser settings to record voice messages.",
        ),
      );
    }
  };

  const removeImage = () => {
    setImageUrl(null);
    resetFileInput();
  };

  const removeAudio = () => setAudioUrl(null);

  const handleImprove = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled || isUploading || isRecording || isImproving) {
      return;
    }
    improveMutation.mutate(
      { text: trimmed },
      {
        onSuccess: (data) => {
          const improved = data.text?.trim() ?? "";
          if (!improved || improved === trimmed) {
            toast(t("Your message already looks good — no changes needed."), {
              icon: "✨",
            });
            return;
          }
          setSuggestion(improved);
        },
      },
    );
  };

  const applySuggestion = () => {
    if (suggestion === null) return;
    setText(suggestion);
    setSuggestion(null);
    inputRef.current?.focus();
  };

  const dismissSuggestion = () => setSuggestion(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (disabled || isUploading || isRecording) return;
    const trimmed = text.trim();
    if (!trimmed && !hasMedia) return;
    onSend?.({
      text: trimmed || undefined,
      imageUrl: imageUrl ?? undefined,
      audioUrl: audioUrl ?? undefined,
    });
    setText("");
    setImageUrl(null);
    setAudioUrl(null);
    resetFileInput();
  };

  const hasContent = text.trim().length > 0 || hasMedia;
  const showTooltip = Boolean(disabled && disabledReason);

  return (
    <div className={styles.wrapper}>
      <div
        id={showTooltip ? TOOLTIP_ANCHOR_ID : undefined}
        className={cn({ [styles.disabledAnchor]: disabled })}
      >
        {(imageUrl || audioUrl || isUploading) && (
          <div className={styles.preview}>
            {imageUrl && (
              <div className={styles.previewThumbWrapper}>
                <img className={styles.previewThumb} src={imageUrl} alt="" />
                {!isUploading && (
                  <button
                    type="button"
                    className={styles.previewRemove}
                    onClick={removeImage}
                    aria-label="Remove image"
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            )}

            {audioUrl && !isUploading && (
              <div className={styles.audioPreview}>
                <audio controls src={audioUrl} className={styles.audioPlayer} />
                <button
                  type="button"
                  className={styles.audioRemove}
                  onClick={removeAudio}
                  aria-label="Remove voice message"
                >
                  <CloseIcon />
                </button>
              </div>
            )}

            {isUploading && !imageUrl && (
              <div className={styles.previewThumbWrapper}>
                <div className={styles.previewLoader}>
                  <ClipLoader size={18} color="#fb3973" />
                </div>
              </div>
            )}
          </div>
        )}

        {suggestion !== null && (
          <div
            className={styles.suggestion}
            role="dialog"
            aria-label={t("AI suggestion")}
          >
            <div className={styles.suggestionHeader}>
              <span className={styles.suggestionTitle}>
                <ImproveIcon className={styles.suggestionTitleIcon} />
                {t("AI suggestion")}
              </span>
              <button
                type="button"
                className={styles.suggestionClose}
                onClick={dismissSuggestion}
                aria-label={t("Dismiss")}
              >
                <DismissIcon />
              </button>
            </div>
            <p className={styles.suggestionText}>{suggestion}</p>
            <div className={styles.suggestionActions}>
              <button
                type="button"
                className={styles.suggestionCancel}
                onClick={dismissSuggestion}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className={styles.suggestionApply}
                onClick={applySuggestion}
              >
                {t("Apply")}
              </button>
            </div>
          </div>
        )}

        <form className={styles.composer} onSubmit={handleSubmit}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={styles.hiddenFileInput}
            onChange={handleFileChange}
          />
          <button
            type="button"
            className={styles.attachButton}
            aria-label="Attach"
            disabled={
              disabled || isUploading || isRecording || hasMedia || isImproving
            }
            onClick={handleAttachClick}
          >
            <AttachIcon />
          </button>

          {isRecording ? (
            <div className={styles.recordingBar}>
              <span className={styles.recordingDot} />
              <span className={styles.recordingTime}>
                {formatDuration(recordSeconds)}
              </span>
              <button
                type="button"
                className={styles.recordingCancel}
                onClick={cancel}
              >
                Cancel
              </button>
            </div>
          ) : (
            <input
              ref={inputRef}
              className={styles.input}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write your message..."
              disabled={disabled || isUploading || isImproving}
            />
          )}

          {!isRecording && (
            <button
              type="button"
              className={styles.improveButton}
              aria-label={t("Improve message with AI")}
              title={t("Improve message with AI")}
              disabled={
                disabled || isUploading || isImproving || !text.trim()
              }
              onClick={handleImprove}
            >
              {isImproving ? (
                <ClipLoader size={16} color="#7a5af8" />
              ) : (
                <ImproveIcon />
              )}
            </button>
          )}

          <button
            type="button"
            className={styles.attachButton}
            aria-label={isRecording ? "Stop recording" : "Record voice"}
            disabled={
              disabled ||
              isUploading ||
              (hasMedia && !isRecording) ||
              isImproving
            }
            onClick={handleMicClick}
          >
            {isRecording ? <StopIcon /> : <MicIcon />}
          </button>

          <button
            type="submit"
            className={cn(styles.sendButton, {
              [styles.sendButtonActive]: hasContent,
            })}
            disabled={
              disabled || isUploading || isRecording || !hasContent || isImproving
            }
            aria-label="Send"
          >
            <SendIcon active={hasContent} />
          </button>
        </form>
      </div>

      {showTooltip && (
        <Tooltip
          anchorSelect={`#${TOOLTIP_ANCHOR_ID}`}
          place="top"
          opacity={1}
          offset={6}
        >
          {disabledReason}
        </Tooltip>
      )}
    </div>
  );
};

export default ChatComposer;
